import { DatePickerInput } from '@unistocks/components/date-picker/date-picker/date-picker-input';
import type {
  DatePickerInputProps,
  DatePickerInputRef,
  DatePickerProps
} from '@unistocks/components/date-picker/date-picker/types';
import { DateStringValue } from '@unistocks/components/calendar/types';
import {
  FormFieldInjectedProps,
  withLabelAndValidation
} from '@unistocks/components/form/components/with-label-and-validation';
import { FormComponentPropsBase, FormFieldOmittedProps } from '@unistocks/components/form/model';
import { t } from '@unistocks/shared/localization/index.ts';
import { createDate, isFunction, today } from '@unistocks/shared/utils/util';
import { memo, useImperativeHandle, useRef } from 'react';
import { FieldValues } from 'react-hook-form';
import { useComposedRefs } from 'tamagui';

export type FormDatepickerProps<TFieldValues extends FieldValues = FieldValues> =
  FormComponentPropsBase<TFieldValues> &
    Omit<DatePickerProps, FormFieldOmittedProps | 'ref'> &
    Omit<DatePickerInputProps, FormFieldOmittedProps | 'required' | 'disabled'>;

export const FormDatepickerComponent = memo<FormDatepickerProps & FormFieldInjectedProps>(
  ({
    ref: refProp,
    name,
    placeholder,
    field,
    fieldState,
    isRequired,
    isDisabled,
    valueTransform,
    dependsOn,
    onChange: onChangeProp,
    ...props
  }) => {
    const datePickerInputRef = useRef<DatePickerInputRef>(null);
    const composedRef = useComposedRefs<DatePickerInputRef>(field.ref, datePickerInputRef, refProp);

    // Expose focus and blur methods to the ref
    useImperativeHandle(field.ref, () => ({
      focus: () => datePickerInputRef.current?.open(),
      blur: () => datePickerInputRef.current?.close()
    }));

    const onValueChange = (date: DateStringValue | null) => {
      let transformedValue = date;
      if (isFunction(valueTransform?.output)) {
        transformedValue = valueTransform.output(date ? createDate(date).toJSDate() : null);
      }

      field.onChange(transformedValue);
      onChangeProp?.(date);
    };

    return (
      <DatePickerInput
        ref={composedRef}
        withArrow={false}
        offset={{ mainAxis: 4 }}

        value={valueTransform?.input?.(field.value) ?? field.value}
        placeholder={placeholder ?? t('::SelectWhat', { 0: t('::Date').toLocaleLowerCase() })}
        disabled={isDisabled}
        required={isRequired}
        invalid={fieldState.invalid}
        onChange={onValueChange}

        highlightToday
        onClear={() => onValueChange(null)}
        presets={[
          {
            label: t('::Today'),
            value: today().toISODate()
          }
        ]}
        {...props}
      />
    );
  }
);

FormDatepickerComponent.displayName = 'FormDatepicker';

export const FormDatepicker = withLabelAndValidation<DatePickerInputRef, FormDatepickerProps>(
  FormDatepickerComponent
) as <TFieldValues extends FieldValues = FieldValues>(
  props: FormDatepickerProps<TFieldValues>
) => React.ReactElement<FormDatepickerProps<TFieldValues>>;
